import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import {
  ArrowLeft, GitBranch, ChevronDown, ChevronRight,
  User, Share2, DollarSign
} from 'lucide-react';

interface TreeNodeData {
  phone: string;
  name: string | null;
  orderValue: number;
  children: TreeNodeData[];
}

function formatCents(cents = 0): string {
  return `HK$${Math.round(cents / 100).toLocaleString('zh-HK')}`;
}

const depthColors = [
  'bg-orange-100 border-orange-200',
  'bg-blue-50 border-blue-200',
  'bg-green-50 border-green-200',
  'bg-purple-50 border-purple-200',
  'bg-gray-50 border-gray-200',
];

function TreeNode({ node, depth = 0 }: { node: TreeNodeData; depth?: number }) {
  const [expanded, setExpanded] = useState(depth < 2);
  const hasChildren = node.children && node.children.length > 0;

  const colorClass = depthColors[depth % depthColors.length];

  return (
    <div className="ml-4 border-l-2 border-gray-200 dark:border-gray-700 pl-4">
      <div className={`flex items-center gap-2 p-2 rounded-lg border ${colorClass}`}>
        {hasChildren && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1 hover:bg-white/50 dark:hover:bg-white/30 rounded"
          >
            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
        )}
        {!hasChildren && <div className="w-7" />}
        <User size={16} className="text-gray-500 dark:text-gray-600" />
        <span className="font-medium text-gray-800 dark:text-gray-800">{node.name || '匿名'}</span>
        <span className="text-gray-500 dark:text-gray-600 text-sm font-mono">{node.phone?.slice(-4)}</span>
        <span className="ml-auto text-sm text-gray-700 dark:text-gray-700 flex items-center gap-1">
          <DollarSign size={14} />
          {formatCents(node.orderValue)}
        </span>
      </div>
      {expanded && hasChildren && (
        <div className="mt-2">
          {node.children.map((child) => (
            <TreeNode key={child.phone} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

const AdminReferralTree: React.FC = () => {
  const { phone } = useParams<{ phone: string }>();
  const navigate = useNavigate();

  const [root, setRoot] = useState<TreeNodeData | null>(null);
  const [profile, setProfile] = useState<{ name?: string | null; referralCode?: string } | null>(null);
  const [maxDepth, setMaxDepth] = useState(5);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
  }, [navigate]);

  useEffect(() => {
    if (!phone) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [treeRes, profileRes] = await Promise.all([
          apiFetch(`/api/public/admin/referrals/${encodeURIComponent(phone)}/tree?maxDepth=${maxDepth}`),
          apiFetch(`/api/public/admin/referrals/${encodeURIComponent(phone)}`),
        ]);
        if (!treeRes.ok) {
          const data = await treeRes.json().catch(() => ({}));
          throw new Error(data.error || '獲取鏈路樹失敗');
        }
        const treeJson = await treeRes.json();
        if (!cancelled) setRoot(treeJson);
        if (profileRes.ok) {
          const profileJson = await profileRes.json();
          if (!cancelled) setProfile(profileJson.profile || null);
        }
      } catch (e: any) {
        if (!cancelled) setError(e.message || '加載失敗');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [phone, maxDepth]);

  const handleLogout = () => {
    if (confirm('確定要登出嗎？')) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      navigate('/admin');
    }
  };

  return (
    <AdminLayout currentPage="referrals" onLogout={handleLogout}>
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/referrals')}
          className="text-orange-600 hover:text-orange-800 text-sm flex items-center gap-1 mb-2"
        >
          <ArrowLeft className="w-4 h-4" /> 返回推薦網絡
        </button>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-900 flex items-center justify-center text-orange-600 dark:text-orange-400 text-xl font-bold">
              {profile?.name?.charAt(0) || root?.name?.charAt(0) || '?'}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
                <GitBranch className="w-6 h-6 text-orange-600" />
                {profile?.name || root?.name || '匿名'} 的推薦鏈路
              </h1>
              <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-3 mt-1">
                <span className="font-mono">{phone}</span>
                {profile?.referralCode && (
                  <span className="flex items-center gap-1"><Share2 className="w-3 h-3" /> {profile.referralCode}</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-white dark:bg-gray-800 p-2 rounded-lg border border-gray-200 dark:border-gray-700">
            <label className="text-sm text-gray-600 dark:text-gray-300">最大深度</label>
            <select
              className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-orange-500 outline-none"
              value={maxDepth}
              onChange={(e) => setMaxDepth(Number(e.target.value))}
            >
              {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
        {loading ? (
          <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">加載中...</div>
        ) : error ? (
          <div className="text-center text-red-500 py-12">{error}</div>
        ) : !root ? (
          <div className="text-center text-gray-500 dark:text-gray-400 py-12">暫無鏈路數據</div>
        ) : (
          <div>
            <div className="mb-4 p-4 rounded-lg bg-orange-100 border border-orange-200">
              <div className="flex items-center gap-2">
                <User size={18} className="text-orange-700" />
                <span className="font-bold text-orange-800">{root.name || '匿名'}</span>
                <span className="text-orange-700 text-sm font-mono">{root.phone}</span>
                <span className="ml-auto text-orange-800 font-medium flex items-center gap-1">
                  <DollarSign size={16} />
                  {formatCents(root.orderValue)}
                </span>
              </div>
            </div>
            <TreeNode node={root} depth={0} />
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminReferralTree;
